import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/YoutubeCompareOverview.graphql';
import { Query, QueryyoutubeCompareOverviewArgs } from '../../__generated__/globalTypes';

export const useYoutubeCompareOverviewQuery = (options?: QueryHookOptions<Query, QueryyoutubeCompareOverviewArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryyoutubeCompareOverviewArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useYoutubeCompareOverviewLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryyoutubeCompareOverviewArgs>
): QueryResult<Query, QueryyoutubeCompareOverviewArgs> & {
  getYoutubeCompareOverview: LazyQueryExecFunction<Query, QueryyoutubeCompareOverviewArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getYoutubeCompareOverview, result] = useLazyQuery<Query, QueryyoutubeCompareOverviewArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getYoutubeCompareOverview, ...result };
};

export const useYoutubeCompareOverviewPromiseQuery = () => {
  const client = useApolloClient();

  const getYoutubeCompareOverview = useCallback(
    (options?: Omit<QueryOptions<QueryyoutubeCompareOverviewArgs, Query>, 'query'>) =>
      client.query<Query, QueryyoutubeCompareOverviewArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getYoutubeCompareOverview };
};
// Suspense Query Hooks
export const useYoutubeCompareOverviewSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryyoutubeCompareOverviewArgs>
) => {
  const result = useSuspenseQuery<Query, QueryyoutubeCompareOverviewArgs>(QUERY, {
    ...options
  });

  return result;
};
